import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Package, Calendar, Wrench, Tag, Building2, 
  DollarSign, FileText, AlertTriangle, X,
  MapPin, Clock, BarChart2, Printer, ScanLine, Users
} from 'lucide-react';
import { ASSET_STATUSES, LOCATIONS } from '../../data/mockData';

const AssetDetails = ({ asset, onClose }) => {
  if (!asset) return null;

  const getStatusColor = (status) => {
    return ASSET_STATUSES[status]?.color || 'gray';
  };

  const getLocationName = (locationId) => {
    return LOCATIONS.find(loc => loc.id === locationId)?.name || locationId;
  };

  const generateQRData = (asset) => {
    return JSON.stringify({
      id: asset.id,
      name: asset.name,
      location: getLocationName(asset.location),
      department: asset.department,
      purchaseDate: asset.purchaseDate,
      currentValue: asset.currentValue,
      status: ASSET_STATUSES[asset.status]?.label || asset.status,
    });
  };

  const printQRCode = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Asset QR Code - ${asset.name}</title>
          <style>
            @page {
              size: 89mm 36mm; /* Standard shipping label size */
              margin: 0;
            }
            
            body {
              font-family: system-ui, -apple-system, sans-serif;
              margin: 0;
              padding: 0;
              width: 89mm;
              height: 36mm;
            }
            
            .label-container {
              display: flex;
              height: 100%;
              padding: 2mm;
              box-sizing: border-box;
            }
            
            .qr-section {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 32mm;
              height: 32mm;
            }
            
            .info-section {
              flex: 1;
              padding-left: 2mm;
              display: flex;
              flex-direction: column;
              justify-content: center;
              font-size: 8pt;
            }
            
            .asset-id {
              font-weight: bold;
              font-size: 10pt;
              color: #000;
              margin-bottom: 1mm;
            }
            
            .asset-name {
              font-weight: bold;
              margin-bottom: 1mm;
              word-break: break-word;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
            }
            
            .info-row {
              display: flex;
              margin-bottom: 0.5mm;
            }
            
            .info-label {
              color: #666;
              width: 20mm;
            }
            
            .info-value {
              flex: 1;
              font-weight: 500;
            }

            @media print {
              html, body {
                width: 89mm;
                height: 36mm;
              }
              
              .label-container {
                break-inside: avoid;
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="label-container">
            <div class="qr-section">
              ${document.getElementById('asset-qr-code').innerHTML}
            </div>
            <div class="info-section">
              <div class="asset-id">${asset.id}</div>
              <div class="asset-name">${asset.name}</div>
              <div class="info-row">
                <span class="info-label">Location:</span>
                <span class="info-value">${getLocationName(asset.location)}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Department:</span>
                <span class="info-value">${asset.department}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Status:</span>
                <span class="info-value">${ASSET_STATUSES[asset.status]?.label || asset.status}</span>
              </div>
            </div>
          </div>
          <script>
            window.onload = () => window.print();
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const getStatusBadgeClass = (status) => {
    const colors = {
      active: 'bg-green-50 text-green-700 border-green-200',
      maintenance: 'bg-orange-50 text-orange-700 border-orange-200',
      inactive: 'bg-red-50 text-red-700 border-red-200',
      pending: 'bg-blue-50 text-blue-700 border-blue-200',
      retired: 'bg-gray-50 text-gray-700 border-gray-200',
      critical: 'bg-red-50 text-red-700 border-red-200'
    };
    return colors[status] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getTagColor = (index) => {
    const colors = [
      'bg-blue-50 text-blue-700 border-blue-200',
      'bg-purple-50 text-purple-700 border-purple-200',
      'bg-pink-50 text-pink-700 border-pink-200',
      'bg-indigo-50 text-indigo-700 border-indigo-200',
      'bg-green-50 text-green-700 border-green-200'
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">{asset.name}</h2>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">{asset.id}</span>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusBadgeClass(asset.status)}`}>
                  {ASSET_STATUSES[asset.status]?.label || asset.status}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/80 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Three columns: Basic Info, Financial Details, and QR Code */}
          <div className="grid grid-cols-3 gap-6">
            {/* Basic Information */}
            <div className="bg-gray-50/50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Package className="w-4 h-4 text-blue-600" />
                Basic Information
              </h3>
              <div className="space-y-3">
                {asset.assignmentType === 'location' ? (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-500" />
                    <span className="text-sm text-gray-900">{getLocationName(asset.location)}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-500" />
                    <div className="text-sm">
                      <span className="text-gray-900">{asset.assignedTo.name}</span>
                      <span className="text-gray-500 block text-xs">{asset.assignedTo.email}</span>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-purple-500" />
                  <span className="text-sm text-gray-900">{asset.department}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-900">
                    Purchased: {new Date(asset.purchaseDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-indigo-500" />
                  <div className="flex flex-wrap gap-1">
                    {asset.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${getTagColor(index)}`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Details */}
            <div className="bg-gray-50/50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-green-600" />
                Financial Details
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-900">
                    Current Value: ${asset.currentValue.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <BarChart2 className="w-4 h-4 text-orange-500" />
                  <span className="text-sm text-gray-900">
                    Depreciation Rate: {(asset.depreciationRate * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-gray-900">
                    Salvage Value: ${asset.salvageValue.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* QR Code Section */}
            <div className="bg-gray-50/50 rounded-xl p-5 flex flex-col items-center text-center">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 w-full flex items-center gap-2 justify-center">
                <ScanLine className="w-4 h-4 text-purple-600" />
                Asset QR Code
              </h3>
              <div className="flex flex-col items-center gap-3 w-full">
                <div 
                  id="asset-qr-code"
                  className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
                >
                  <QRCodeSVG
                    value={generateQRData(asset)}
                    size={120}
                    level="H"
                    includeMargin={true}
                    style={{ display: 'block' }}
                  />
                </div>
                <button
                  onClick={printQRCode}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-[#43302b] text-white rounded-lg hover:bg-[#43302b]/90 font-medium w-full justify-center transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  Print Label
                </button>
              </div>
            </div>
          </div>

          {/* Maintenance Info */}
          <div className="bg-gray-50/50 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-orange-600" />
              Maintenance History
            </h3>
            <div className="space-y-3">
              {asset.maintenanceHistory.map((maintenance, index) => (
                <div 
                  key={index}
                  className="p-4 bg-white rounded-lg border border-gray-200 flex items-center justify-between shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-50 rounded-lg">
                      <Wrench className="w-4 h-4 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{maintenance.type}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        {new Date(maintenance.date).toLocaleDateString()}
                        <DollarSign className="w-3 h-3" />
                        ${maintenance.cost.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">{maintenance.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Documents */}
          <div className="bg-gray-50/50 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-indigo-600" />
              Documents
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {asset.documents.map((doc, index) => (
                <button
                  key={index}
                  className="p-4 bg-white rounded-lg border border-gray-200 flex items-center gap-3 hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <div className="p-2 bg-indigo-50 rounded-lg">
                    <FileText className="w-4 h-4 text-indigo-500" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="uppercase">{doc.type}</span>
                      <span>•</span>
                      <span>{doc.size}</span>
                      <span>•</span>
                      <span>Added {new Date(doc.uploadDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-white transition-colors"
          >
            Close
          </button>
          <button className="px-4 py-2 bg-[#43302b] text-white rounded-lg hover:bg-[#43302b]/90 transition-colors">
            Edit Asset
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetDetails; 